<template lang="pug">
.individual-match
  .sub-page-padding
    .container
      .main
        .main-panel(v-if="matchDetail")
          h2.main-title 個人戦
          button.dummy-toggle-button(v-if="isDevelopment" type="button" @click="useDummyData = !useDummyData") {{ useDummyData ? '実データを表示' : 'ダミーデータを表示' }}
          .rank-graph-block(v-if="showRankGraph")
            RankGraph(:values="rankGraphValues")
            .graph-days
              span(v-for="(day, index) in rankGraphDayLabels" :key="`day-${index}`") {{ day }}
            p.graph-period
              | 順位: {{ graphPeriodStartDateTime | formatDate }} 〜 {{ graphPeriodEndDateTime | formatDate }}
          .status-row(v-if="showStatus")
            .status-item
              .label 順位
              .value
                span.main-number {{ latestRank | number }}
                span.sub-number /{{ maxRank | number }}位
            .status-item
              .label 平均歩数
              .value
                span.main-number {{ latestAverageSteps | number }}
                span.unit 歩
          ul.daily-steps(v-if="displayStepRows.length")
            li.daily-step-row(v-for="row in displayStepRows" :key="row.dateKey")
              .step-label {{ row.label }}
              .step-value
                span.step-number {{ row.stepsText }}
                span.step-unit(v-if="row.stepsText !== '-'") 歩
          p.no-data(v-else) 歩数データがありません
        p.no-data(v-else) 個人戦データを取得できませんでした
        button.overview-button(type="button" @click="isOverviewOpen = true") 開催概要
      .overview-overlay(v-if="isOverviewOpen" @click="isOverviewOpen = false")
      .side(:class="{ 'is-open': isOverviewOpen }")
        button.overview-close(type="button" @click="isOverviewOpen = false") 閉じる
        MatchOverview(v-if="matchDetail" :matchDetail="matchDetail")
</template>

<script>
import moment from 'moment-timezone'
import RankGraph from '@/components/TeamMatch/RankGraph'
import MatchOverview from '@/components/TeamMatch/MatchOverview'

const DAILY_ROW_COUNT = 5
const DUMMY_RANK_HISTORY = [120, 100, 22, 88, 62, 60]
const DUMMY_STEPS_HISTORY = [6200, 7000, 9200, 7800, 8080, 8080]
const DUMMY_MAX_RANK = 300
const DUMMY_DAILY_STEPS = [940, 7040, 10540, 7735, 8245]

export default {
  name: 'individualMatch',
  components: {
    RankGraph,
    MatchOverview,
  },
  data() {
    return {
      isOverviewOpen: false,
      matchDetail: null,
      stepsData: [],
      useDummyData: false,
    }
  },
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    },
    matchId() {
      return this.$route.params.id
    },
    isGeneral() {
      const raw = this.$route.query.isGeneral
      if (raw === true || raw === 'true' || raw === '1' || raw === 1) {
        return true
      }
      if (raw === false || raw === 'false' || raw === '0' || raw === 0) {
        return false
      }
      return false
    },
    hasStatistics() {
      return !!(this.matchDetail && this.matchDetail.statistics)
    },
    showStatus() {
      return this.useDummyData || this.hasStatistics
    },
    rankHistory() {
      if (!this.hasStatistics || !Array.isArray(this.matchDetail.statistics.rankHistory)) {
        return []
      }
      return this.matchDetail.statistics.rankHistory
    },
    stepsHistory() {
      if (!this.hasStatistics || !Array.isArray(this.matchDetail.statistics.stepsHistory)) {
        return []
      }
      return this.matchDetail.statistics.stepsHistory
    },
    activeRankHistory() {
      if (this.useDummyData) {
        return DUMMY_RANK_HISTORY
      }
      return this.rankHistory
    },
    activeStepsHistory() {
      if (this.useDummyData) {
        return DUMMY_STEPS_HISTORY
      }
      return this.stepsHistory
    },
    activeMaxRank() {
      if (this.useDummyData) {
        return DUMMY_MAX_RANK
      }
      if (!this.hasStatistics) {
        return 0
      }
      return this.matchDetail.statistics.maxRank || 0
    },
    rankStartDate() {
      if (this.useDummyData) {
        const days = Math.max(this.activeRankHistory.length - 1, 0)
        return moment()
          .subtract(days + 1, 'days')
          .startOf('day')
      }
      if (!this.hasStatistics) {
        return null
      }
      return moment(this.matchDetail.statistics.startDateTime).startOf('day')
    },
    rankWindow() {
      if (!this.rankStartDate || this.activeRankHistory.length === 0) {
        return null
      }
      const startDate = this.rankStartDate.clone()
      const matchEndDate = moment(this.matchDetail.endDateTime - 1).startOf('day')
      const yesterday = moment()
        .subtract(1, 'days')
        .startOf('day')
      const latestDate = moment.min(matchEndDate, yesterday)

      if (latestDate.isBefore(startDate, 'day')) {
        return {
          slotDates: [],
          values: [],
          availableCount: 0,
        }
      }

      const daysSinceStart = latestDate.diff(startDate, 'days') + 1
      const availableCount = Math.max(0, Math.min(this.activeRankHistory.length, daysSinceStart))

      const slotDates = []
      const values = []

      if (availableCount >= 7) {
        const baseIndex = availableCount - 7
        for (let i = 0; i < 7; i += 1) {
          slotDates.push(startDate.clone().add(baseIndex + i, 'days'))
          values.push(
            this.activeRankHistory[baseIndex + i] == null
              ? null
              : this.activeRankHistory[baseIndex + i]
          )
        }
      } else {
        for (let i = 0; i < 7; i += 1) {
          slotDates.push(startDate.clone().add(i, 'days'))
          if (i < availableCount) {
            values.push(this.activeRankHistory[i] == null ? null : this.activeRankHistory[i])
          } else {
            values.push(null)
          }
        }
      }

      return {
        slotDates,
        values,
        availableCount,
      }
    },
    rankGraphValues() {
      if (!this.rankWindow) {
        return []
      }
      return this.rankWindow.values
    },
    rankGraphDayLabels() {
      if (!this.rankWindow || !this.rankWindow.slotDates.length) {
        return []
      }
      const days = ['日', '月', '火', '水', '木', '金', '土']
      return this.rankWindow.slotDates.map(date => days[date.day()])
    },
    showRankGraph() {
      if (!this.rankWindow) {
        return false
      }
      return this.rankWindow.availableCount > 0
    },
    graphPeriodStartDateTime() {
      if (!this.rankWindow || !this.rankWindow.availableCount) {
        return null
      }
      return this.rankWindow.slotDates[0].valueOf()
    },
    graphPeriodEndDateTime() {
      if (!this.rankWindow || !this.rankWindow.availableCount) {
        return null
      }
      return this.rankWindow.slotDates[this.rankWindow.slotDates.length - 1].valueOf()
    },
    latestRank() {
      if (!this.rankWindow || this.rankWindow.availableCount === 0) {
        return 0
      }
      return this.activeRankHistory[this.rankWindow.availableCount - 1] || 0
    },
    maxRank() {
      return this.activeMaxRank
    },
    latestAverageSteps() {
      if (!this.activeStepsHistory.length) {
        return 0
      }
      return this.activeStepsHistory[this.activeStepsHistory.length - 1] || 0
    },
    stepMapByDate() {
      const map = {}
      this.stepsData.forEach(item => {
        const dateKey = moment(item.dateTime).format('YYYY-MM-DD')
        map[dateKey] = item.steps
      })
      return map
    },
    displayStepRows() {
      if (!this.matchDetail) {
        return []
      }
      if (this.useDummyData) {
        const rows = []
        for (let i = 0; i < DAILY_ROW_COUNT; i += 1) {
          const day = moment()
            .subtract(i, 'days')
            .startOf('day')
          rows.push({
            dateKey: day.format('YYYY-MM-DD'),
            label: this.toStepLabel(day),
            stepsText: Number(DUMMY_DAILY_STEPS[i] || 0).toLocaleString(),
          })
        }
        return rows
      }
      const endDate = moment(Math.min(Date.now(), this.matchDetail.endDateTime - 1)).startOf('day')
      const rows = []
      for (let i = 0; i < DAILY_ROW_COUNT; i += 1) {
        const day = endDate.clone().subtract(i, 'days')
        const dateKey = day.format('YYYY-MM-DD')
        const steps = this.stepMapByDate[dateKey]
        const label = this.toStepLabel(day)
        rows.push({
          dateKey,
          label,
          stepsText: steps == null ? '-' : Number(steps).toLocaleString(),
        })
      }
      return rows
    },
  },
  methods: {
    async loadMatchDetail() {
      if (!this.matchId) {
        this.matchDetail = null
        return
      }
      const response = await this.serverCode('getIndividualMatch', {
        individualMatchID: this.matchId,
        isGeneral: this.isGeneral,
      })
      if (!response || !response.succeed) {
        this.matchDetail = null
        return
      }
      this.matchDetail = response
    },
    loadStepsData() {
      if (!this.matchDetail) {
        this.stepsData = []
        return Promise.resolve()
      }
      const fromDateTime = moment(this.matchDetail.startDateTime)
        .startOf('day')
        .valueOf()
      const toDateTime = moment(this.matchDetail.endDateTime - 1)
        .startOf('day')
        .valueOf()
      return new Promise(resolve => {
        this.api(
          'getHealthDataByRange',
          {
            fromDateTime,
            toDateTime,
          },
          rv => {
            this.stepsData = rv && rv.stepsData ? rv.stepsData : []
            resolve()
          }
        )
      })
    },
    async loadPageData() {
      await this.loadMatchDetail()
      await this.loadStepsData()
    },
    toStepLabel(dayMoment) {
      const today = moment().startOf('day')
      const yesterday = moment()
        .subtract(1, 'days')
        .startOf('day')
      if (dayMoment.isSame(today, 'day')) {
        return '今日の歩数'
      }
      if (dayMoment.isSame(yesterday, 'day')) {
        return '昨日の歩数'
      }
      return dayMoment.format('M月D日（dd）')
    },
  },
  created() {
    this.loadPageData()
  },
  beforeRouteUpdate(to, from, next) {
    this.loadPageData().finally(() => {
      next()
    })
  },
}
</script>

<style scoped lang="scss">
.individual-match {
  .container {
    display: flex;
    gap: 60px;
    align-items: flex-start;
  }

  .main {
    flex: 0 0 660px;
    max-width: 660px;
  }

  .main-panel {
    border-top: 3px solid $base-color-red;
    padding-top: 14px;
  }

  .main-title {
    margin: 0 0 16px;
    font-size: 34px;
    font-weight: bold;
    color: $base-color;
  }

  .dummy-toggle-button {
    margin-bottom: 14px;
    padding: 8px 12px;
    border: 1px solid #d6d6d6;
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    color: $base-color;
  }

  .rank-graph-block {
    border-bottom: 1px solid #e5e5e5;
    padding-bottom: 14px;
  }

  .graph-days {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
    font-size: 18px;
    color: #888;
  }

  .graph-period {
    margin: 8px 0 0;
    text-align: center;
    font-size: 14px;
    color: #888;
  }

  .status-row {
    margin-top: 16px;
    border: 1px solid #ff95b8;
    border-radius: 8px;
    display: flex;
  }

  .status-item {
    flex: 1;
    padding: 12px 18px;

    &:first-child {
      border-right: 1px solid #f2d6df;
    }

    .label {
      font-size: 14px;
      color: $base-color;
      margin-bottom: 8px;
    }

    .value {
      color: $base-color;
      font-family: $font-gotham-bold;
      line-height: 1;
    }
  }

  .main-number {
    font-size: 40px;
  }

  .sub-number,
  .unit {
    margin-left: 4px;
    font-size: 18px;
  }

  .daily-steps {
    list-style: none;
    margin: 20px 0 0;
    padding: 0;
    border-top: 1px solid #eee;
  }

  .daily-step-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    border-bottom: 1px solid #eee;
  }

  .step-label {
    font-size: 26px;
    color: $base-color;
  }

  .step-value {
    font-family: $font-gotham-bold;
    color: $base-color;
  }

  .step-number {
    font-size: 40px;
  }

  .step-unit {
    margin-left: 4px;
    font-size: 22px;
  }

  .no-data {
    margin: 20px 0;
    color: #888;
    font-size: 14px;
  }

  .side {
    flex: 0 0 360px;
    max-width: 360px;
  }

  .overview-button,
  .overview-close,
  .overview-overlay {
    display: none;
  }

  @include sp-layout {
    .container {
      display: block;
    }

    .main {
      width: 100%;
      max-width: none;
    }

    .main-title {
      font-size: 28px;
      margin-bottom: 12px;
    }

    .dummy-toggle-button {
      margin-bottom: 10px;
      font-size: 12px;
    }

    .graph-days {
      font-size: 14px;
    }

    .graph-period {
      font-size: 12px;
    }

    .status-item {
      padding: 10px 12px;

      .label {
        font-size: 12px;
        margin-bottom: 6px;
      }
    }

    .main-number {
      font-size: 28px;
    }

    .sub-number,
    .unit {
      font-size: 14px;
    }

    .daily-step-row {
      padding: 12px 0;
    }

    .step-label {
      font-size: 15px;
    }

    .step-number {
      font-size: 30px;
    }

    .step-unit {
      font-size: 14px;
    }

    .overview-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-top: 16px;
      padding: 10px 16px;
      border: 1px solid #ccc;
      border-radius: 6px;
      background-color: #fff;
    }

    .overview-overlay {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      z-index: 1000;
    }

    .side {
      display: block;
      position: fixed;
      right: 0;
      top: 0;
      width: 86%;
      max-width: 360px;
      height: 100vh;
      padding: 20px 16px;
      background: #fff;
      z-index: 1001;
      transform: translateX(100%);
      transition: transform 0.2s ease;
      overflow-y: auto;
    }

    .side.is-open {
      transform: translateX(0);
    }

    .overview-close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 12px;
      padding: 8px 14px;
      border: 1px solid #ccc;
      border-radius: 6px;
      background-color: #fff;
    }
  }
}
</style>
